import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Unstable_Grid2';
import * as Yup from 'yup';


import { InputAdornment, MenuItem, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { updateUserDetails, useGetAllPermissions, useGetAllRoles, UserResponse } from 'src/api/account';
import { scrollToFirstError } from 'src/auth/context/jwt/utils';
import { useAuthContext } from 'src/auth/hooks';
import { RHFAutocomplete, RHFSelect, RHFTextField } from 'src/components/hook-form';
import FormProvider from 'src/components/hook-form/form-provider';
import Iconify from 'src/components/iconify';
import { PHONE_REGEX_EXPRESSION } from 'src/config-global';
import { ICityResponse } from 'src/types/user';
import { useGetRestaurants } from 'src/api/restaurant';

// ----------------------------------------------------------------------

const phoneRegExp = PHONE_REGEX_EXPRESSION

export default function UserAccountGeneral({ user_profile }: { user_profile: UserResponse }) {

  const { enqueueSnackbar } = useSnackbar();
  const { restaurants, restaurantsLoading } = useGetRestaurants();
  const { roles, rolesLoading } = useGetAllRoles();

  const UpdateRestaurantSchema = Yup.object().shape({
    phone_number: Yup.string()
      .required('Contact number is required')
      .matches(phoneRegExp, 'Invalid phone number')
      .min(8, 'Phone number is too short')
      .max(8, 'Phone number is too long'),
    id: Yup.number().required('User Id is required'),
    restaurants: Yup.array(),
    permissions: Yup.array(),
    role: Yup.number().required('Role is required'),
  });

  const defaultValues = useMemo(
    () => ({
      phone_number: user_profile.phone_number || '',
      id: user_profile.id || 0,
      restaurants: user_profile.restaurants || [],
      permissions: user_profile.permissions || [],
      role: user_profile.role?.id,
    }),
    [user_profile]
  );

  const methods = useForm({
    mode: 'all',
    resolver: yupResolver(UpdateRestaurantSchema),
    defaultValues,
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  useEffect(() => {
    methods.reset(defaultValues);
  }, [defaultValues, methods]);

  const onSubmit = handleSubmit(
    async (data) => {
      try {
        const form_data = {
          phone_number: data.phone_number,
          restaurants: data.restaurants,
          role: { id: data.role },
        };
        console.log(form_data);

        const response = await updateUserDetails(user_profile.id, form_data);
        if (response) {
          enqueueSnackbar('User details updated successfully', { variant: 'success' });
        }
      } catch (error) {
        console.error(error);
        enqueueSnackbar('Something went wrong', { variant: 'error' });
      }
    },
    (e) => {
      scrollToFirstError(e);
    }
  );

  return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      <Grid container mt={3} spacing={3}>
        <Grid xs={12} md={12}>
          <Card sx={{ p: 3 }}>
            <Typography variant="h5" sx={{ my: 3 }}>
              General Details
            </Typography>

            <Box
              rowGap={3}
              columnGap={2}
              display="grid"
              gridTemplateColumns={{
                xs: 'repeat(1, 1fr)',
                sm: 'repeat(1, 1fr)',
              }}
            >
              <RHFTextField
                name="phone_number"
                label="Phone Number"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Iconify icon="twemoji:flag-kuwait" sx={{ color: 'text.disabled' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* {user?.type === "Superuser" && <RHFSelect
                            name="status"
                            label="Status"
                            sx={{ mt: 3 }}
                        >
                            <MenuItem value="verified">Verified</MenuItem>
                            <MenuItem value="unverified">Unverified</MenuItem>
                            <MenuItem value="inactive">Inactive</MenuItem>
                            <MenuItem value="deleted">Deleted</MenuItem>
                        </RHFSelect>} */}

            <RHFTextField disabled name="id" label="User Id" sx={{ mt: 3 }} />

            <RHFSelect
              name="role"
              label="Role"
              sx={{ mt: 3 }}
              defaultValue={user_profile.role?.id.toString()}
            >
              {roles?.map((role) => (
                <MenuItem key={role.id} value={role.id}>
                  {role.role}
                </MenuItem>
              ))}
            </RHFSelect>

            <RHFAutocomplete
              loadingText="Loading Restaurants..."
              name="restaurants"
              label="Restaurants"
              options={restaurants || []}
              getOptionLabel={(option: any) =>
                typeof option === "string" ? option : option?.name_en ?? ''}
              sx={{ mt: 3 }}
              isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
              loading={restaurantsLoading}
              multiple
              renderOption={(props: any, option: any) => (
                <Box component="li" {...props}>
                  <Typography variant="subtitle2">{option.name_en}</Typography>
                </Box>
              )}
              onChange={(event: any, value: any) => {
                methods.setValue('restaurants', value);
              }}
            />

            <Stack spacing={3} alignItems="flex-end" sx={{ mt: 3 }}>
              <LoadingButton type="submit" variant="contained" loading={isSubmitting}>
                Update User Details
              </LoadingButton>
            </Stack>
          </Card>
        </Grid>
      </Grid>
    </FormProvider>
  );
}









